// frontend/src/components/calendar/UpcomingSidebar.js
import React from 'react';
import {
    Box,
    Typography,
    Card,
    Divider,
    Button,
    Stack,
    Avatar,
    AvatarGroup
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import YouTubeIcon from '@mui/icons-material/YouTube';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FacebookIcon from '@mui/icons-material/Facebook';
import RedditIcon from '@mui/icons-material/Reddit';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { PLATFORM_COLORS, PLATFORM_CONTENT_FIELD } from './CALENDAR_CONFIG';

const PLATFORM_ICONS = {
    youtube: YouTubeIcon,
    instagram: InstagramIcon,
    twitter: TwitterIcon,
    linkedin: LinkedInIcon,
    facebook: FacebookIcon,
    reddit: RedditIcon,
};

const UpcomingSidebar = ({ events, onEventClick }) => {
    const navigate = useNavigate();

    // Filter to events this week
    const now = dayjs();
    const weekEnd = now.endOf('week');
    const upcomingThisWeek = events
        .filter(e => {
            const date = dayjs(e.start);
            return (date.isAfter(now) || date.isSame(now, 'day')) && (date.isBefore(weekEnd) || date.isSame(weekEnd, 'day'));
        })
        .sort((a, b) => dayjs(a.start).diff(dayjs(b.start)));

    // Month stats
    const uniquePlatforms = new Set();
    events.forEach(e => {
        e.resource.platforms.forEach(p => uniquePlatforms.add(p));
    });

    return (
        <Box data-testid="upcoming-sidebar">
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
                Upcoming Posts
            </Typography>

            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.2 }}>
                THIS WEEK
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {upcomingThisWeek.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                    <CalendarTodayIcon sx={{ fontSize: 40, color: 'action.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                        Nothing scheduled for this week.
                    </Typography>
                </Box>
            ) : (
                <Stack spacing={2} sx={{ mb: 4 }}>
                    {upcomingThisWeek.map(event => {
                        const { resource } = event;
                        const primaryPlatform = resource.platforms[0];
                        const contentField = PLATFORM_CONTENT_FIELD[primaryPlatform];
                        const contentPreview = resource.platformContent[primaryPlatform]?.[contentField] || 'No content';

                        return (
                            <Card
                                key={event.id}
                                data-testid="upcoming-post-item"
                                onClick={() => onEventClick(resource)}
                                sx={{
                                    p: 2,
                                    cursor: 'pointer',
                                    borderLeft: `4px solid ${PLATFORM_COLORS[primaryPlatform]}`,
                                    '&:hover': { boxShadow: 3 }
                                }}
                            >
                                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                                    <AvatarGroup max={3}>
                                        {resource.platforms.map(p => {
                                            const Icon = PLATFORM_ICONS[p];
                                            return (
                                                <Avatar
                                                    key={p}
                                                    sx={{
                                                        width: 24,
                                                        height: 24,
                                                        bgcolor: PLATFORM_COLORS[p],
                                                        '& svg': { fontSize: 14 }
                                                    }}
                                                >
                                                    <Icon />
                                                </Avatar>
                                            );
                                        })}
                                    </AvatarGroup>
                                    <Box>
                                        <Typography variant="caption" fontWeight="bold" display="block">
                                            {dayjs(event.start).format('ddd, MMM D')}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {dayjs(event.start).format('h:mm A')}
                                        </Typography>
                                    </Box>
                                </Stack>
                                <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                                    {contentPreview}
                                </Typography>
                            </Card>
                        );
                    })}
                </Stack>
            )}

            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.2 }}>
                MONTH SUMMARY
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Box sx={{ mb: 4 }} data-testid="month-summary">
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {events.length} posts scheduled
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Across {uniquePlatforms.size} platform(s)
                </Typography>
            </Box>

            <Button
                variant="contained"
                fullWidth
                startIcon={<AddIcon />}
                onClick={() => navigate('/app/post')}
                data-testid="schedule-new-btn"
                sx={{
                    borderRadius: 3,
                    py: 1.5,
                    background: 'linear-gradient(45deg, #6366f1, #818cf8)',
                    boxShadow: '0 4px 14px 0 rgba(99, 102, 241, 0.39)',
                }}
            >
                Schedule New Post
            </Button>
        </Box>
    );
};

export default UpcomingSidebar;
